import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Check if a date is in the past (before today)
export const isPastDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

// Get effective business hours for a specific date, considering overrides
export const getEffectiveHoursForDate = (dateStr, businessHours, hoursOverrides = []) => {
  if (!businessHours || !businessHours.hours) return null;
  
  const date = new Date(dateStr);
  const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  // Check for overrides (in priority order)
  for (const override of hoursOverrides) {
    let matches = false;
    
    if (override.override_type === 'single' && override.single_date === dateStr) {
      matches = true;
    } else if (override.override_type === 'range') {
      if (dateStr >= override.start_date && dateStr <= override.end_date) {
        matches = true;
      }
    } else if (override.override_type === 'recurring_annual') {
      if (override.recurring_month === month && override.recurring_day === day) {
        matches = true;
      }
    }
    
    if (matches) {
      // For date ranges with per-day hours enabled
      if (override.override_type === 'range' && override.use_per_day_hours && override.per_day_hours) {
        const dayHours = override.per_day_hours.find(d => d.day === dayOfWeek);
        if (dayHours) {
          return {
            day: dayOfWeek,
            is_open: !dayHours.is_closed,
            open_time: dayHours.open_time || '00:00',
            close_time: dayHours.close_time || '23:59',
            override: override, // Include override info for display
          };
        }
      }
      
      // Standard override (same hours for all days)
      return {
        day: dayOfWeek,
        is_open: !override.is_closed,
        open_time: override.open_time || '00:00',
        close_time: override.close_time || '23:59',
        override: override, // Include override info for display
      };
    }
  }
  
  // No override, use default business hours
  const defaultHours = businessHours.hours.find(h => h.day === dayOfWeek);
  return defaultHours || { day: dayOfWeek, is_open: false, open_time: '00:00', close_time: '00:00' };
};

// Color mappings for employees
export const EMPLOYEE_COLORS = [
  { id: 'coral', name: 'Coral', bg: 'bg-orange-100 dark:bg-orange-950', border: 'border-orange-300 dark:border-orange-700', text: 'text-orange-900 dark:text-orange-100', bar: 'bg-orange-400 dark:bg-orange-500', avatar: 'avatar-coral', hex: '#fb923c' },
  { id: 'mint', name: 'Mint', bg: 'bg-emerald-100 dark:bg-emerald-950', border: 'border-emerald-300 dark:border-emerald-700', text: 'text-emerald-900 dark:text-emerald-100', bar: 'bg-emerald-400 dark:bg-emerald-500', avatar: 'avatar-mint', hex: '#34d399' },
  { id: 'lavender', name: 'Lavender', bg: 'bg-violet-100 dark:bg-violet-950', border: 'border-violet-300 dark:border-violet-700', text: 'text-violet-900 dark:text-violet-100', bar: 'bg-violet-400 dark:bg-violet-500', avatar: 'avatar-lavender', hex: '#a78bfa' },
  { id: 'sky', name: 'Sky', bg: 'bg-sky-100 dark:bg-sky-950', border: 'border-sky-300 dark:border-sky-700', text: 'text-sky-900 dark:text-sky-100', bar: 'bg-sky-400 dark:bg-sky-500', avatar: 'avatar-sky', hex: '#38bdf8' },
  { id: 'rose', name: 'Rose', bg: 'bg-rose-100 dark:bg-rose-950', border: 'border-rose-300 dark:border-rose-700', text: 'text-rose-900 dark:text-rose-100', bar: 'bg-rose-400 dark:bg-rose-500', avatar: 'avatar-rose', hex: '#fb7185' },
  { id: 'amber', name: 'Amber', bg: 'bg-amber-100 dark:bg-amber-950', border: 'border-amber-300 dark:border-amber-700', text: 'text-amber-900 dark:text-amber-100', bar: 'bg-amber-400 dark:bg-amber-500', avatar: 'avatar-amber', hex: '#fbbf24' },
  { id: 'teal', name: 'Teal', bg: 'bg-teal-100 dark:bg-teal-950', border: 'border-teal-300 dark:border-teal-700', text: 'text-teal-900 dark:text-teal-100', bar: 'bg-teal-400 dark:bg-teal-500', avatar: 'avatar-teal', hex: '#2dd4bf' },
  { id: 'indigo', name: 'Indigo', bg: 'bg-indigo-100 dark:bg-indigo-950', border: 'border-indigo-300 dark:border-indigo-700', text: 'text-indigo-900 dark:text-indigo-100', bar: 'bg-indigo-400 dark:bg-indigo-500', avatar: 'avatar-indigo', hex: '#818cf8' },
  { id: 'pink', name: 'Pink', bg: 'bg-pink-100 dark:bg-pink-950', border: 'border-pink-300 dark:border-pink-700', text: 'text-pink-900 dark:text-pink-100', bar: 'bg-pink-400 dark:bg-pink-500', avatar: 'avatar-pink', hex: '#f472b6' },
  { id: 'lime', name: 'Lime', bg: 'bg-lime-100 dark:bg-lime-950', border: 'border-lime-300 dark:border-lime-700', text: 'text-lime-900 dark:text-lime-100', bar: 'bg-lime-400 dark:bg-lime-500', avatar: 'avatar-lime', hex: '#a3e635' },
  { id: 'cyan', name: 'Cyan', bg: 'bg-cyan-100 dark:bg-cyan-950', border: 'border-cyan-300 dark:border-cyan-700', text: 'text-cyan-900 dark:text-cyan-100', bar: 'bg-cyan-400 dark:bg-cyan-500', avatar: 'avatar-cyan', hex: '#22d3ee' },
  { id: 'fuchsia', name: 'Fuchsia', bg: 'bg-fuchsia-100 dark:bg-fuchsia-950', border: 'border-fuchsia-300 dark:border-fuchsia-700', text: 'text-fuchsia-900 dark:text-fuchsia-100', bar: 'bg-fuchsia-400 dark:bg-fuchsia-500', avatar: 'avatar-fuchsia', hex: '#e879f9' },
];

export const getColorById = (colorId) => {
  // Handle custom hex colors
  if (colorId && colorId.startsWith('#')) {
    return {
      id: 'custom',
      name: 'Custom',
      bg: 'bg-gray-100 dark:bg-gray-900',
      border: 'border-gray-300 dark:border-gray-700',
      text: 'text-gray-900 dark:text-gray-100',
      bar: '', // Will use inline style
      avatar: 'avatar-custom',
      hex: colorId,
      isCustom: true
    };
  }
  return EMPLOYEE_COLORS.find(c => c.id === colorId) || EMPLOYEE_COLORS[0];
};

// Time utilities
export const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

export const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

export const formatTimeRange = (start, end) => {
  return `${formatTime(start)} - ${formatTime(end)}`;
};

// Calculate duration in hours (returns number, not string)
export const calculateDurationHours = (start, end) => {
  const startMins = timeToMinutes(start);
  const endMins = timeToMinutes(end);
  return (endMins - startMins) / 60;
};

// Calculate duration in hours (formatted string for display)
export const calculateDuration = (start, end) => {
  return calculateDurationHours(start, end).toFixed(2);
};

// Days of week
export const DAYS_OF_WEEK = [
  { id: 'monday', name: 'Monday', short: 'Mon' },
  { id: 'tuesday', name: 'Tuesday', short: 'Tue' },
  { id: 'wednesday', name: 'Wednesday', short: 'Wed' },
  { id: 'thursday', name: 'Thursday', short: 'Thu' },
  { id: 'friday', name: 'Friday', short: 'Fri' },
  { id: 'saturday', name: 'Saturday', short: 'Sat' },
  { id: 'sunday', name: 'Sunday', short: 'Sun' },
];

export const getDayName = (date) => {
  return date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
};

// Check if two time ranges overlap (with optional gap requirement)
export const doTimesOverlap = (start1, end1, start2, end2, gapMinutes = 0) => {
  const s1 = timeToMinutes(start1);
  const e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  const e2 = timeToMinutes(end2);
  
  // With gap: ranges must be at least gapMinutes apart
  // Range 1 ends, then gap, then Range 2 starts (or vice versa)
  // Overlap/too-close if: s1 < (e2 + gap) AND s2 < (e1 + gap)
  return s1 < (e2 + gapMinutes) && s2 < (e1 + gapMinutes);
};

// Check if a shift overlaps with any other shifts for the same employee on the same date
// excludeShiftId: ID of shift to exclude from check (for editing existing shifts)
// gapMinutes: Required gap between shifts
export const checkShiftOverlap = (shifts, employeeId, date, startTime, endTime, excludeShiftId = null, gapMinutes = 0) => {
  const employeeShifts = shifts.filter(s => 
    s.employee_id === employeeId && 
    s.date === date && 
    s.id !== excludeShiftId
  );
  
  for (const shift of employeeShifts) {
    if (doTimesOverlap(startTime, endTime, shift.start_time, shift.end_time, gapMinutes)) {
      return shift; // Return the overlapping/too-close shift
    }
  }
  
  return null; // No overlap
};

// Check if employee already has a shift on this date (for single shift mode)
export const employeeHasShiftOnDate = (shifts, employeeId, date, excludeShiftId = null) => {
  return shifts.find(s => 
    s.employee_id === employeeId && 
    s.date === date && 
    s.id !== excludeShiftId
  );
};

// Currency options
export const CURRENCY_OPTIONS = [
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
];

export const getCurrencySymbol = (code) => {
  const currency = CURRENCY_OPTIONS.find(c => c.code === code);
  return currency?.symbol || code;
};

// Calculate unpaid break duration in hours for a shift
export const calculateUnpaidBreakHours = (shift, breakTypes = []) => {
  if (!shift.breaks || shift.breaks.length === 0) return 0;
  
  return shift.breaks.reduce((total, brk) => {
    const breakType = breakTypes.find(bt => bt.id === brk.break_type_id);
    // Only deduct if break is unpaid (is_paid === false)
    if (breakType && !breakType.is_paid) {
      const breakMins = timeToMinutes(brk.end_time) - timeToMinutes(brk.start_time);
      return total + (breakMins / 60);
    }
    return total;
  }, 0);
};

// Calculate billable hours (total shift minus unpaid breaks)
export const calculateBillableHours = (shift, breakTypes = []) => {
  const totalHours = calculateDurationHours(shift.start_time, shift.end_time);
  const unpaidBreakHours = calculateUnpaidBreakHours(shift, breakTypes);
  return totalHours - unpaidBreakHours;
};

// Get the effective hourly rate for a rate name
export const getRateValue = (rateName, employee) => {
  if (!employee) return 0;
  
  // If rate name specified, find it in employee's additional rates
  if (rateName && employee.additional_rates) {
    const additionalRate = employee.additional_rates.find(r => r.name === rateName);
    if (additionalRate) return additionalRate.rate;
  }
  
  // Default to employee's standard hourly rate
  return employee.hourly_rate || 0;
};

// Get the effective hourly rate for a shift (handles additional rates)
export const getShiftRate = (shift, employee) => {
  if (!employee) return 0;
  
  // If shift has a specific rate name, find it in employee's additional rates
  if (shift.rate_name && employee.additional_rates) {
    const additionalRate = employee.additional_rates.find(r => r.name === shift.rate_name);
    if (additionalRate) return additionalRate.rate;
  }
  
  // Default to employee's standard hourly rate
  return employee.hourly_rate || 0;
};

// Calculate daily salary cost for salary-based employees
export const getSalaryDailyCost = (employee) => {
  if (!employee || employee.pay_type !== "salary") return 0;
  
  const amount = employee.salary_amount || 0;
  const period = employee.salary_period || "annual";
  
  switch (period) {
    case "weekly":
      return amount / 7; // Daily rate from weekly
    case "monthly":
      return amount / 30; // Daily rate from monthly (approximate)
    case "annual":
      return amount / 365; // Daily rate from annual
    default:
      return 0;
  }
};

// Calculate weekly salary cost for salary-based employees
export const getSalaryWeeklyCost = (employee) => {
  if (!employee || employee.pay_type !== "salary") return 0;
  
  const amount = employee.salary_amount || 0;
  const period = employee.salary_period || "annual";
  
  switch (period) {
    case "weekly":
      return amount;
    case "monthly":
      return (amount * 12) / 52; // Weekly rate from monthly
    case "annual":
      return amount / 52; // Weekly rate from annual
    default:
      return 0;
  }
};

// Calculate daily cost for a paid absence
export const calculatePaidAbsenceDailyCost = (absence, employee) => {
  if (!absence || !absence.is_paid || !employee) return 0;
  
  // For salary employees, use daily salary cost
  if (employee.pay_type === "salary") {
    return getSalaryDailyCost(employee);
  }
  
  // For fixed amount (e.g., statutory sick pay)
  if (absence.paid_hours_type === "fixed_amount" && absence.fixed_amount > 0) {
    if (absence.fixed_amount_period === "weekly") {
      return absence.fixed_amount / 7; // Daily rate from weekly
    }
    // Default to daily amount
    return absence.fixed_amount;
  }
  
  // For hourly employees, calculate based on hours
  let dailyHours = 0;
  
  if (absence.paid_hours_type === "contracted" && employee.has_contracted_hours && employee.min_hours > 0) {
    // Use contracted hours (weekly min_hours / 5 days)
    dailyHours = employee.min_hours / 5;
  } else if (absence.custom_paid_hours) {
    // Use custom hours specified
    dailyHours = absence.custom_paid_hours;
  }
  
  return dailyHours * (employee.hourly_rate || 0);
};

// Calculate total cost for a paid absence (multi-day)
export const calculatePaidAbsenceTotalCost = (absence, employee) => {
  if (!absence || !absence.is_paid || !employee) return 0;
  
  const dailyCost = calculatePaidAbsenceDailyCost(absence, employee);
  
  // Calculate number of days
  const startDate = new Date(absence.start_date);
  const endDate = new Date(absence.end_date);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  
  return dailyCost * days;
};

// Calculate cost for a single rate segment (considering breaks that overlap)
const calculateSegmentCost = (segment, employee, shiftBreaks = [], breakTypes = []) => {
  const segmentStart = timeToMinutes(segment.start_time);
  const segmentEnd = timeToMinutes(segment.end_time);
  const segmentDuration = (segmentEnd - segmentStart) / 60;
  
  // Calculate unpaid break time that overlaps with this segment
  let unpaidBreakHours = 0;
  if (shiftBreaks && shiftBreaks.length > 0) {
    for (const brk of shiftBreaks) {
      const breakType = breakTypes.find(bt => bt.id === brk.break_type_id);
      if (breakType && !breakType.is_paid) {
        const brkStart = timeToMinutes(brk.start_time);
        const brkEnd = timeToMinutes(brk.end_time);
        
        // Calculate overlap between break and segment
        const overlapStart = Math.max(segmentStart, brkStart);
        const overlapEnd = Math.min(segmentEnd, brkEnd);
        if (overlapStart < overlapEnd) {
          unpaidBreakHours += (overlapEnd - overlapStart) / 60;
        }
      }
    }
  }
  
  const billableHours = segmentDuration - unpaidBreakHours;
  const rate = getRateValue(segment.rate_name, employee);
  return billableHours * rate;
};

// Calculate shift cost (handles split rates, breaks, etc.)
export const calculateShiftCost = (startTime, endTime, hourlyRate, shift = null, breakTypes = [], employee = null) => {
  if (!shift || !employee) {
    // Fallback for simple calculation
    const duration = calculateDurationHours(startTime, endTime);
    return duration * (hourlyRate || 0);
  }
  
  // Check if shift has rate segments (split shift)
  if (shift.rate_segments && shift.rate_segments.length > 0) {
    // Sum cost of each segment
    return shift.rate_segments.reduce((total, segment) => {
      return total + calculateSegmentCost(segment, employee, shift.breaks, breakTypes);
    }, 0);
  }
  
  // Non-split shift: use single rate for whole shift
  const effectiveRate = getShiftRate(shift, employee);
  
  if (breakTypes.length > 0) {
    const billableHours = calculateBillableHours(shift, breakTypes);
    return billableHours * effectiveRate;
  }
  
  // Fallback for simple calculation without breaks
  const duration = calculateDurationHours(startTime, endTime);
  return duration * effectiveRate;
};

// Calculate total hours for shifts (uses raw hours for accuracy)
export const calculateTotalHours = (shifts) => {
  return shifts.reduce((total, shift) => {
    return total + calculateDurationHours(shift.start_time, shift.end_time);
  }, 0);
};

// Calculate total billable hours (excluding unpaid breaks)
export const calculateTotalBillableHours = (shifts, breakTypes = []) => {
  return shifts.reduce((total, shift) => {
    return total + calculateBillableHours(shift, breakTypes);
  }, 0);
};
